// src/pages/HomePage.js
import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { booksAPI, compilationsAPI } from '../services/api';
import { useApp } from '../context/AppContext';
import { Chip, SectionLabel, ProgressBar } from '../components/ui';
import BookCard from '../components/library/BookCard';
import BookListCard from '../components/library/BookListCard';
import { decrypt, decryptData } from '../utils/security';

// SUBJECTS loaded dynamically from API via booksAPI.getFilters()

const GRADIENTS = [
  'linear-gradient(150deg,#D4A5A3,#C08A88)',
  'linear-gradient(150deg,#A3B4CC,#8898B8)',
  'linear-gradient(150deg,#A3C4B4,#88AE9E)',
  'linear-gradient(150deg,#C4B488,#AE9E74)',
  'linear-gradient(150deg,#B4A3C8,#9E88B4)',
  'linear-gradient(150deg,#C4A3B4,#AE8E9E)',
];

// COMPILATIONS loaded from API

const HomePage = ({ onNav, onOpenBook }) => {
  
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSubject, setActive] = useState('All');
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [subjects, setSubjects] = useState(['All']);
  const [compilations, setCompilations] = useState([]);
  const [groupBooks, setGroupBooks] = useState([]);
  const { currentUser, bookmarks, getToken } = useApp();

  const [selectedBookModal, setSelectedBookModal] = useState(null);

  // ── Browse List Modal State ──
  const [browseListOpen, setBrowseListOpen] = useState(false);
  const [browseListBooks, setBrowseListBooks] = useState([]);
  const [browseListLoading, setBrowseListLoading] = useState(false);
  const [browseListSearch, setBrowseListSearch] = useState('');

  const loadBrowseList = async () => {
    setBrowseListOpen(true);
    if (browseListBooks.length > 0) return; // already loaded
    setBrowseListLoading(true);
    try {
      const token = await getToken();
      let allBooks = [];
      let pg = 1;
      let hasMore = true;
      while (hasMore) {
        const res = await booksAPI.getBooks(token, { page: pg, limit: 100 });
        const fetched = decryptData(res.data || []);
        allBooks = [...allBooks, ...fetched];
        hasMore = res.pagination?.has_next || false;
        pg++;
        if (pg > 50) break; // safety cap
      }
      setBrowseListBooks(allBooks);
    } catch (e) {
      console.error('[BrowseList] Error fetching books:', e);
    } finally {
      setBrowseListLoading(false);
    }
  };

  // Load subjects from API
  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const res = await booksAPI.getFilters();
        const topics = res.data?.topics || res.data?.categories || [];
        setSubjects(['All', ...topics.slice(0, 8)]);
      } catch { }
    })();
  }, [getToken]);

  // Load group books — directly by ID
  useEffect(() => {
    if (!currentUser?.group_books?.length) return;
    (async () => {
      try {
        const token = await getToken();
        // Fetch each group book individually
        const gbIds = currentUser.group_books || [];
        const results = await Promise.all(
          gbIds.map(id =>
            booksAPI.getBook(token, id).then(r => r.data).catch(() => null)
          )
        );
        setGroupBooks(decryptData(results.filter(Boolean)));
      } catch {}
    })();
  }, [currentUser?.group_books?.length, getToken]);

  // Load compilations preview from API
  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const res = await compilationsAPI.getAll(token, { page: 1, limit: 3 });
        setCompilations(decryptData(res.data || []));
      } catch { }
    })();
  }, [getToken]);

  const fetchBooks = async (subject, pg = 1) => {
    setLoading(true);
    try {
      const token = await getToken();
      const params = { page: pg, limit: 18 };
      if (subject !== 'All') params.topic = subject;
      const res = await booksAPI.getBooks(token, params);
      // FIX: decrypt once here (bulk, on fetch) instead of calling decrypt()
      // inline inside JSX on every render — AES decrypt is CPU-heavy and
      // was running for every visible book on every single re-render.
      setBooks(decryptData(res.data || []));
      setPagination(res.pagination);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchBooks(activeSubject, 1); setPage(1); }, [activeSubject, getToken]);

  // Group books — currentUser ke groupIds se milne wali books highlight hongi
  // Backend already merges group books into access_books
  // Hum simply currentUser.groupIds > 0 hone pe badge dikhate hain
  // Since we don't have per-book group info on frontend, we mark books
  // that are NOT in personal access_books but ARE accessible (came from group)
  const personalBookIds = new Set((currentUser?.personal_access_books || currentUser?.access_books || []).map(String));
  const isGroupBook = (bookId) => {
    return (currentUser?.groupIds || []).length > 0 && !personalBookIds.has(String(bookId));
  };

  const inProgress = books.filter(b => {
    const p = currentUser?.progress?.[String(b._id)];
    return p && p.percent > 0;
  });

  return (
    <div className="animate-fade-in pb-20 md:pb-8" style={{ background: 'var(--off-white)', minHeight: '100vh' }}>

      {/* ── New Editions Banner ── */}
      <div className="mx-4 mt-4 md:mx-6 flex items-center gap-3 rounded-xl px-4 py-3 border"
        style={{ background: 'linear-gradient(135deg,var(--rose-pale),var(--slate-pale))', borderColor: 'var(--rose-lt)' }}>
        <span style={{ fontSize: 20 }}>📬</span>
        <div className="flex-1">
          <div className="font-bold" style={{ fontSize: 11, color: 'var(--rose)', fontFamily: 'Nunito,sans-serif' }}>New Edition Available</div>
          <div style={{ fontSize: 10, color: 'var(--ink-mid)', fontFamily: 'Nunito,sans-serif' }}>
            {pagination ? `${pagination.total} books` : '—'} in your library · Welcome back, {currentUser?.name || 'Advocate'}
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={loadBrowseList}
            id="browse-list-btn"
            className="rounded-md px-3 py-1.5 font-bold text-[10px] border-0 cursor-pointer"
            style={{
              background: 'linear-gradient(135deg, #b83e40, #8b2c2e)',
              color: 'white',
              fontFamily: 'Nunito,sans-serif',
              boxShadow: '0 2px 8px rgba(139,44,46,0.45)',
              letterSpacing: 0.3,
            }}>
            📋 Browse List
          </button>
          <button onClick={() => onNav('search')}
            className="rounded-md px-3 py-1.5 font-bold text-[10px] border cursor-pointer"
            style={{ background: 'transparent', color: 'var(--rose)', borderColor: 'var(--rose)', fontFamily: 'Nunito,sans-serif' }}>
            Browse →
          </button>
        </div>
      </div>

      {/* ── Subject Chips ── */}
      <div className="flex gap-2 overflow-x-auto px-4 md:px-6 py-3 no-scrollbar">
        {subjects.map(s => (
          <Chip key={s} label={s} active={activeSubject === s} onClick={() => setActive(s)} />
        ))}
      </div>

      {/* ── Continue Reading ── */}
      {inProgress.length > 0 && (
        <>
          <SectionLabel text="CONTINUE READING" />
          <div className="px-4 md:px-6 space-y-2 mb-2">
            {inProgress.slice(0, 3).map(book => {
              const prog = currentUser?.progress?.[String(book._id)];
              const idx = (book._id) % GRADIENTS.length;
              return (
                <div key={book._id} className="flex items-center gap-3 bg-white rounded-xl p-3 border cursor-pointer hover:shadow-sm transition-all"
                  style={{ borderColor: 'var(--border)' }} onClick={() => onOpenBook(book)}>
                  <div className="rounded-lg flex-shrink-0 flex items-center justify-center"
                    style={{ width: 44, height: 60, background: GRADIENTS[Math.abs(idx)], position: 'relative', overflow: 'hidden' }}>
                    <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: 'rgba(255,255,255,.2)' }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold truncate" style={{ fontSize: 12, color: 'var(--ink)', fontFamily: 'Nunito,sans-serif' }}>
                      {decrypt(book.book_name || book.book_title)}
                    </div>
                    <ProgressBar value={prog?.percent || 0} className="mt-1 mb-1" />
                    <div style={{ fontSize: 10, color: 'var(--ink-lt)', fontFamily: 'Nunito,sans-serif' }}>{prog?.percent || 0}% complete</div>
                  </div>
                  <button className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-white"
                    style={{ background: 'var(--rose)' }}>▶</button>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* ── Group Books Section ── */}
      {currentUser?.group_books?.length > 0 && groupBooks.length > 0 && (
        <>
          <SectionLabel text={<span>🗂️ YOUR GROUP BOOKS <span style={{ marginLeft:6, fontSize:9, background:'var(--rose-pale)', color:'var(--rose)', padding:'2px 8px', borderRadius:10, fontWeight:700, border:'1px solid var(--rose-lt)' }}>ASSIGNED</span></span>} />
          <div className="flex gap-3 px-4 md:px-6 pb-3 overflow-x-auto no-scrollbar">
            {groupBooks.map((book, i) => (
              <BookCard key={book._id} book={book} onClick={() => onOpenBook(book)}
                isBookmarked={bookmarks.includes(String(book._id))}
                progress={currentUser?.progress?.[String(book._id)]?.percent || 0}
                animDelay={i * 0.04} />
            ))}
          </div>
        </>
      )}

      {/* ── All Books Vertical List ── */}
      <SectionLabel text={activeSubject === 'All' ? 'ALL BOOKS' : `${activeSubject.toUpperCase()} BOOKS`} />
      
      {loading ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 px-4 md:px-6 pb-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="animate-pulse bg-white border rounded-xl" style={{ height: 180, borderColor: 'var(--border)' }}></div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 px-4 md:px-6 pb-6">
          {books.map((book) => (
            <BookListCard 
              key={book._id} 
              book={book} 
              onClick={() => setSelectedBookModal(book)}
              onOpenFullBook={() => onOpenBook(book)}
            />
          ))}
        </div>
      )}

      {/* ── Article Compilations Shelf ── */}
      <SectionLabel text={<span>📋 ARTICLE COMPILATIONS <span className="ml-1 px-1.5 py-0.5 rounded text-[9px] font-bold border"
        style={{ background: 'var(--rose-pale)', color: 'var(--rose)', borderColor: 'var(--rose-lt)', fontFamily: 'Nunito,sans-serif' }}>EXCLUSIVE</span></span>}
        action="Browse all →" onAction={() => onNav('compilations')} />
      <div className="flex gap-3 px-4 md:px-6 pb-3 overflow-x-auto no-scrollbar">
        {compilations.map((comp, i) => {
          const grad = GRADIENTS[i % GRADIENTS.length];
          const title = comp.book_name || comp.book_title || comp.title || '—';
          const topic = (comp.topics || []).map(t => t.name).join(', ');
          return (
            <div key={comp._id || comp.id || i} className="flex-shrink-0 cursor-pointer group" style={{ width: 130 }}
              onClick={() => onNav('compilations')}>
              <div className="rounded-xl relative overflow-hidden shadow-xs group-hover:shadow-md group-hover:-translate-y-0.5 transition-all"
                style={{ width: 130, height: 80, background: grad }}>
                <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, background: 'rgba(255,255,255,.2)' }} />
                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(transparent 30%,rgba(44,44,53,.45))' }} />
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '6px 8px' }}>
                  <div className="font-bold text-white line-clamp-2" style={{ fontSize: 8, fontFamily: 'Nunito,sans-serif', lineHeight: 1.3 }}>
                    {decrypt(title)}
                  </div>
                </div>
              </div>
              <div className="mt-1.5">
                <div className="font-ui text-ink-mid line-clamp-2 group-hover:text-rose transition-colors"
                  style={{ fontSize: 10, fontFamily: 'Nunito,sans-serif', lineHeight: 1.3 }}>{decrypt(title)}</div>
                {topic && <div style={{ fontSize: 9, color: 'var(--ink-faint)', fontFamily: 'Nunito,sans-serif' }}>{topic}</div>}
              </div>
            </div>
          )
        })}
      </div>

      {/* ── Archive Teaser ── */}
      <SectionLabel text="📦 ARCHIVE" action="Browse archive →" onAction={() => onNav('archive')} />
      <div className="px-4 md:px-6 mb-4">
        <div className="flex items-center gap-4 bg-white rounded-xl border p-4 cursor-pointer hover:shadow-sm transition-all"
          style={{ borderColor: 'var(--border)' }} onClick={() => onNav('archive')}>
          <div className="flex-shrink-0 rounded-lg flex items-center justify-center"
            style={{ width: 44, height: 60, background: 'linear-gradient(135deg,#C4A882,#B4946E)' }}>
            <span style={{ fontSize: 20 }}>⏱</span>
          </div>
          <div className="flex-1">
            <div className="font-bold uppercase" style={{ fontSize: 9, color: 'var(--sand)', letterSpacing: 1.5, fontFamily: 'Nunito,sans-serif' }}>110+ Year Archive</div>
            <div className="font-bold" style={{ fontSize: 13, color: 'var(--ink)', fontFamily: 'Nunito,sans-serif' }}>Historic Commentary Editions</div>
            <div style={{ fontSize: 11, color: 'var(--ink-lt)', fontFamily: 'Nunito,sans-serif', marginTop: 2 }}>All superseded editions preserved</div>
          </div>
          <span style={{ color: 'var(--ink-lt)', fontSize: 18 }}>›</span>
        </div>
      </div>

      {/* ── LexAssist Teaser ── */}
      <div className="px-4 md:px-6 mb-4">
        <div className="flex items-center gap-4 bg-white rounded-xl border p-4 cursor-pointer hover:shadow-sm transition-all"
          style={{ borderColor: 'var(--border)', borderLeft: '3px solid var(--lavender)' }}
          onClick={() => onNav('lexassist')}>
          <div className="flex-shrink-0 rounded-xl flex items-center justify-center text-xl"
            style={{ width: 44, height: 44, background: '#1E1A2E' }}>◈</div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-0.5">
              <span className="font-bold" style={{ fontSize: 13, color: 'var(--ink)', fontFamily: 'Nunito,sans-serif' }}>LexAssist AI</span>
              <span className="font-bold px-1.5 py-0.5 rounded text-[9px]"
                style={{ background: 'var(--lavender-pale)', color: 'var(--lavender)', fontFamily: 'Nunito,sans-serif' }}>PHASE 3</span>
            </div>
            <div style={{ fontSize: 11, color: 'var(--ink-lt)', fontFamily: 'Nunito,sans-serif' }}>
              AI research · AIR corpus only · No internet · Source-cited
            </div>
          </div>
          <span style={{ color: 'var(--ink-lt)', fontSize: 18 }}>›</span>
        </div>
      </div>

      {/* ── Stats ── */}
      <div className="grid grid-cols-3 gap-3 px-4 md:px-6 pb-6">
        {[
          { label: 'Commentaries', value: '50+', icon: '📖', nav: null },
          { label: 'Bare Acts', value: '200+', icon: '⚖️', nav: null },
          { label: 'Archive', value: '400+', icon: '🏛', nav: 'archive' },
        ].map(stat => (
          <div key={stat.label} className="bg-white rounded-xl border p-3 text-center cursor-pointer hover:shadow-sm transition-all"
            style={{ borderColor: 'var(--border)' }}
            onClick={() => stat.nav && onNav(stat.nav)}>
            <div style={{ fontSize: 22, marginBottom: 4 }}>{stat.icon}</div>
            <div className="font-bold font-display" style={{ fontSize: 17, color: 'var(--rose)' }}>{stat.value}</div>
            <div style={{ fontSize: 10, color: 'var(--ink-lt)', fontFamily: 'Nunito,sans-serif' }}>{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {pagination && pagination.total_pages > 1 && (
        <div className="flex justify-center gap-2 px-4 pb-6">
          <button disabled={!pagination.has_prev}
            onClick={() => { const np = page - 1; setPage(np); fetchBooks(activeSubject, np); }}
            className="px-4 py-2 bg-white border rounded-lg text-xs font-bold font-ui disabled:opacity-40"
            style={{ borderColor: 'var(--border)', color: 'var(--ink-mid)', fontFamily: 'Nunito,sans-serif' }}>
            ‹ Prev
          </button>
          <span className="px-4 py-2 text-xs font-ui" style={{ color: 'var(--ink-lt)', fontFamily: 'Nunito,sans-serif' }}>
            {pagination.page} / {pagination.total_pages}
          </span>
          <button disabled={!pagination.has_next}
            onClick={() => { const np = page + 1; setPage(np); fetchBooks(activeSubject, np); }}
            className="px-4 py-2 bg-white border rounded-lg text-xs font-bold font-ui disabled:opacity-40"
            style={{ borderColor: 'var(--border)', color: 'var(--ink-mid)', fontFamily: 'Nunito,sans-serif' }}>
            Next ›
          </button>
        </div>
      )}

      {/* ── Browse List Modal ── */}
      {browseListOpen && (
        <div
          id="browse-list-modal"
          className="fixed inset-0 z-50 flex items-end md:items-center justify-center"
          style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)' }}
          onClick={() => setBrowseListOpen(false)}
        >
          <div
            className="bg-white w-full md:w-[88%] md:max-w-[1400px] rounded-t-2xl md:rounded-2xl overflow-hidden shadow-2xl flex flex-col"
            style={{ maxHeight: '92vh' }}
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-5 pt-4 pb-3 border-b" style={{ borderColor: 'var(--border)', background: 'linear-gradient(135deg, var(--rose-pale) 0%, white 100%)' }}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="font-bold" style={{ fontSize: 15, color: 'var(--ink)', fontFamily: 'Nunito,sans-serif' }}>
                    📚 Your Subscribed Books
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--ink-lt)', fontFamily: 'Nunito,sans-serif' }}>
                    {browseListLoading ? 'Loading…' : (
                      browseListSearch.trim()
                        ? `${browseListBooks.filter(b => (b.book_name || b.book_title || b.title || '').toLowerCase().includes(browseListSearch.toLowerCase())).length} results for "${browseListSearch}"`
                        : `${browseListBooks.length} books available`
                    )}
                  </div>
                </div>
                <button
                  onClick={() => { setBrowseListOpen(false); setBrowseListSearch(''); }}
                  className="w-9 h-9 flex items-center justify-center rounded-full border-0 cursor-pointer transition-all hover:scale-110"
                  style={{ background: 'var(--rose)', color: 'white', fontSize: 13, fontFamily: 'Nunito,sans-serif', boxShadow: '0 2px 8px rgba(201,123,119,0.4)' }}
                >
                  ✕
                </button>
              </div>

              {/* Search Box */}
              <div className="relative" style={{ marginBottom: 2 }}>
                <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: browseListSearch ? 'var(--rose)' : 'var(--ink-faint)', fontSize: 14, transition: 'color 0.2s' }}>
                  🔍
                </div>
                <input
                  id="browse-list-search"
                  type="text"
                  placeholder="Search by book name…"
                  value={browseListSearch}
                  onChange={e => setBrowseListSearch(e.target.value)}
                  autoComplete="off"
                  className="w-full pl-9 pr-10 py-2.5 rounded-xl border outline-none transition-all"
                  style={{
                    fontSize: 12,
                    fontFamily: 'Nunito,sans-serif',
                    color: 'var(--ink)',
                    background: 'white',
                    borderColor: browseListSearch ? 'var(--rose)' : 'var(--border)',
                    boxShadow: browseListSearch ? '0 0 0 3px rgba(201,123,119,0.15)' : '0 1px 4px rgba(0,0,0,0.06)',
                  }}
                />
                {browseListSearch && (
                  <button
                    onClick={() => setBrowseListSearch('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center rounded-full border-0 cursor-pointer"
                    style={{ background: 'var(--rose)', color: 'white', fontSize: 9 }}
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>

            {/* Book List */}
            <div className="overflow-y-auto flex-1 px-4 py-3">
              {browseListLoading ? (
                <div className="space-y-2 py-2">
                  {[...Array(8)].map((_, i) => (
                    <div key={i} className="animate-pulse h-10 rounded-xl" style={{ background: 'var(--rose-pale)', opacity: 1 - i * 0.1 }} />
                  ))}
                </div>
              ) : (() => {
                const q = browseListSearch.trim().toLowerCase();
                const filtered = q
                  ? browseListBooks.filter(b => (b.book_name || b.book_title || b.title || '').toLowerCase().includes(q))
                  : browseListBooks;

                if (browseListBooks.length === 0) return (
                  <div className="text-center py-14" style={{ color: 'var(--ink-lt)', fontSize: 12, fontFamily: 'Nunito,sans-serif' }}>
                    No books found in your subscription.
                  </div>
                );

                if (filtered.length === 0) return (
                  <div className="text-center py-14">
                    <div style={{ fontSize: 32, marginBottom: 10 }}>🔍</div>
                    <div className="font-bold" style={{ fontSize: 13, color: 'var(--ink)', fontFamily: 'Nunito,sans-serif', marginBottom: 4 }}>No results found</div>
                    <div style={{ fontSize: 11, color: 'var(--ink-lt)', fontFamily: 'Nunito,sans-serif' }}>Try a different keyword</div>
                  </div>
                );

                return (
                  <ol className="space-y-1" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {filtered.map((book, idx) => {
                      const title = book.book_name || book.book_title || book.title || '—';
                      const origIdx = browseListBooks.indexOf(book);
                      // Highlight matched text
                      let displayTitle;
                      if (q) {
                        const i = title.toLowerCase().indexOf(q);
                        if (i !== -1) {
                          displayTitle = (
                            <span>
                              {title.slice(0, i)}
                              <mark style={{ background: 'rgba(201,123,119,0.25)', color: 'var(--rose)', borderRadius: 3, padding: '0 2px', fontWeight: 800 }}>
                                {title.slice(i, i + q.length)}
                              </mark>
                              {title.slice(i + q.length)}
                            </span>
                          );
                        } else {
                          displayTitle = title;
                        }
                      } else {
                        displayTitle = title;
                      }
                      return (
                        <li
                          key={book._id}
                          id={`browse-book-${book._id}`}
                          className="flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer group"
                          style={{
                            background: idx % 2 === 0 ? 'var(--off-white)' : 'white',
                            border: '1px solid var(--border-lt)',
                            transition: 'all 0.15s ease',
                          }}
                          onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--rose-lt)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(201,123,119,0.12)'; e.currentTarget.style.transform = 'translateX(2px)'; }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-lt)'; e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'none'; }}
                          onClick={() => { onOpenBook(book); setBrowseListOpen(false); setBrowseListSearch(''); }}
                        >
                          <span className="flex-shrink-0 font-bold text-center rounded-md"
                            style={{ fontSize: 9, color: 'var(--rose)', background: 'var(--rose-pale)', width: 26, height: 26, lineHeight: '26px', fontFamily: 'Nunito,sans-serif', flexShrink: 0 }}>
                            {origIdx + 1}
                          </span>
                          <span className="flex-1 font-bold" style={{ fontSize: 12, color: 'var(--ink)', fontFamily: 'Nunito,sans-serif', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {displayTitle}
                          </span>
                          <span style={{ color: 'var(--rose)', fontSize: 16, opacity: 0.5, flexShrink: 0 }}>›</span>
                        </li>
                      );
                    })}
                  </ol>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* ── Book Details Modal ── */}
      {selectedBookModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={() => setSelectedBookModal(null)}
        >
          <div 
            className="bg-white rounded-2xl max-w-2xl w-full flex flex-col md:flex-row overflow-hidden shadow-2xl relative"
            onClick={(e) => e.stopPropagation()}
            style={{ maxHeight: '90vh' }}
          >
            {/* Close Button */}
            <button 
              onClick={() => setSelectedBookModal(null)}
              className="absolute top-4 right-4 z-10 w-8 h-8 flex items-center justify-center bg-white/80 backdrop-blur rounded-full text-black hover:bg-gray-200 transition-colors"
            >
              ✕
            </button>
            
            {/* Image Section */}
            {(() => {
              const rawCoverModal = selectedBookModal.images?.large || selectedBookModal.images?.small || selectedBookModal.category?.cover_image_large || null;
              const modalCoverImage = (rawCoverModal && (rawCoverModal.startsWith('data:image') || rawCoverModal.startsWith('http'))) ? rawCoverModal : null;
              return (
                <div 
                  className="w-full md:w-2/5 flex-shrink-0 min-h-[250px] relative"
                  style={{
                    background: modalCoverImage
                      ? `url("${modalCoverImage}") center/cover` 
                      : GRADIENTS[Math.abs((selectedBookModal._id || selectedBookModal.id || 0) % GRADIENTS.length)]
                  }}
                >
                  {/* Spine shadow for premium 3D book depth */}
                  <div className="absolute left-0 top-0 bottom-0 w-1.5" style={{ background:'linear-gradient(90deg, rgba(0,0,0,0.15), transparent)' }}/>
                  <div className="absolute left-1.5 top-0 bottom-0 w-0.5" style={{ background:'rgba(255,255,255,.15)' }}/>

                  {!modalCoverImage && (
                    <div className="absolute inset-0 flex items-center justify-center p-6 text-center" style={{ background: 'linear-gradient(transparent 10%, rgba(0,0,0,0.65))' }}>
                      <div className="font-bold text-white text-xl" style={{ fontFamily: 'Nunito,sans-serif', textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
                        {decrypt(selectedBookModal.book_name || selectedBookModal.book_title || selectedBookModal.title || '—')}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Details Section */}
            <div className="w-full md:w-3/5 p-6 md:p-8 flex flex-col overflow-y-auto bg-[#fafafa]">
              <div className="mb-6 flex-1">
                <h2 className="text-2xl font-bold mb-2 leading-tight" style={{ color: 'var(--ink)', fontFamily: 'Playfair Display, serif' }}>
                  {decrypt(selectedBookModal.book_name || selectedBookModal.book_title || selectedBookModal.title || '—')}
                </h2>
                
                <h4 className="font-bold text-md mb-1 uppercase tracking-wide" style={{ color: 'var(--ink)' }}>Publisher:</h4>
                <div className="text-sm font-semibold text-gray-600 mb-6 uppercase tracking-wider">
                  {selectedBookModal.publishers?.length > 0 ? selectedBookModal.publishers.map(p => decrypt(p.name || p.short_name || '')).join(', ') : 'AIR Law Academy'}
                </div>

                <div className="prose prose-sm max-w-none" style={{ color: 'var(--ink-mid)', fontFamily: 'Nunito,sans-serif', lineHeight: 1.6 }}>
                  <h4 className="font-bold text-md mb-2 uppercase tracking-wide" style={{ color: 'var(--ink)' }}>Key Features:</h4>
                  {selectedBookModal.book_summary ? (
                    <div dangerouslySetInnerHTML={{ __html: selectedBookModal.book_summary.replace(/\n/g, '<br/>') }} />
                  ) : (
                    <p>Comprehensive legal commentary and study notes prepared specifically for ease of referencing.</p>
                  )}
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-200">
                <button 
                  onClick={() => {
                    onOpenBook(selectedBookModal);
                    setSelectedBookModal(null);
                  }}
                  className="w-full py-3.5 text-white font-bold rounded-lg transition-transform active:scale-95 shadow-md hover:shadow-lg"
                  style={{ background: 'linear-gradient(135deg, var(--rose), #b83e40)', fontSize: 15, fontFamily: 'Nunito,sans-serif' }}
                >
                  📖 READ FULL BOOK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;